// Frequency utilities for recurring templates (frontend)
import type { Frequency } from '@/types/database'

/**
 * Monthly multipliers for converting frequency amounts to monthly equivalents
 */
export const MONTHLY_MULTIPLIER: Record<Frequency, number> = {
  'weekly': 52 / 12,       // ~4.33 payments per month
  'bi-weekly': 26 / 12,    // ~2.17 payments per month
  'semi-monthly': 2,       // 2 payments per month
  'monthly': 1,            // 1 payment per month
  'quarterly': 1 / 3,      // ~0.33 payments per month
  'annual': 1 / 12         // ~0.083 payments per month
}

/**
 * Human-readable labels for frequency selector
 */
export const FREQUENCY_LABELS: Record<Frequency, string> = {
  'weekly': 'Weekly',
  'bi-weekly': 'Bi-weekly (every 2 weeks)',
  'semi-monthly': 'Semi-monthly (twice a month)',
  'monthly': 'Monthly',
  'quarterly': 'Quarterly',
  'annual': 'Annual'
}

/**
 * Short labels for compact displays
 */
export const FREQUENCY_SHORT_LABELS: Record<Frequency, string> = {
  'weekly': 'Weekly',
  'bi-weekly': 'Bi-weekly',
  'semi-monthly': '2x/month',
  'monthly': 'Monthly',
  'quarterly': 'Quarterly',
  'annual': 'Annual'
}

/**
 * Day of week labels (Sunday = 0)
 */
export const DAY_OF_WEEK_LABELS = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday'
]

/**
 * Calculate monthly equivalent amount
 */
export function getMonthlyAmount(amount: number, frequency: Frequency): number {
  return amount * MONTHLY_MULTIPLIER[frequency]
}

/**
 * Format amount with frequency context
 */
export function formatAmountWithFrequency(amount: number, frequency: Frequency): string {
  const monthly = getMonthlyAmount(amount, frequency)
  const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' })

  if (frequency === 'monthly') {
    return `${formatter.format(amount)}/mo`
  }

  return `${formatter.format(amount)} ${FREQUENCY_SHORT_LABELS[frequency]} (~${formatter.format(monthly)}/mo)`
}

/**
 * All frequency options for dropdowns
 */
export const FREQUENCY_OPTIONS: Frequency[] = [
  'weekly',
  'bi-weekly',
  'semi-monthly',
  'monthly',
  'quarterly',
  'annual'
]
